/**
 * Where users are told to reach a human, and where the documentation lives.
 *
 * Both are quoted at users from more than one place, and a value that is right in
 * one and stale in another is worse than having no fallback at all. The FAQ page
 * carried its own copy of the address with a "TODO: replace with your real support
 * address" beside it; this is that one place.
 */
import { openChat } from './crisp';

export const SUPPORT_EMAIL = 'support@zonstaff.com';

/** The user-facing documentation. */
export const DOCS_URL =
  'https://zon.gitbook.io/zon-staff/zon-staff-management/how-to-set-up-zon-staff-on-shopify';

/**
 * Reach a human: the live chat, falling back to email when it cannot load.
 *
 * The chatbox is back and now arrives carrying the merchant's identity, so a
 * chat is worth far more to both sides than a mail draft. Email remains the
 * fallback for ad blockers and corporate networks, which do block
 * client.crisp.chat.
 *
 * `subject` carries the question the caller was asked from: it becomes the
 * chat's opening message, or the mail subject. Three call sites pass this
 * straight to onClick, where React supplies a SyntheticEvent as the first
 * argument, so the guard is on the type rather than on truthiness.
 */
export function openSupport(subject) {
  const topic = typeof subject === 'string' && subject.trim() ? subject.trim() : '';

  const toEmail = () => {
    const query = topic ? `?subject=${encodeURIComponent(topic)}` : '';
    window.location.href = `mailto:${SUPPORT_EMAIL}${query}`;
  };

  // .catch as well as the false branch: if openChat throws outright - no
  // document.head in some embedded context, a blocked script insertion - the
  // click must still reach a human rather than do nothing at all.
  openChat(topic).then((reachedChat) => {
    if (!reachedChat) toEmail();
  }).catch(toEmail);
}

/**
 * Per-page documentation, keyed by route prefix (lower-case, leading slash).
 *
 * Each page used to carry its own "visit our Knowledge Base" link to these, which
 * meant two documentation links on every screen once the footer gained one. The
 * pages dropped theirs and the footer resolves through here instead, so someone on
 * Payroll still reaches the payroll guide rather than the generic setup page.
 *
 * Prefix match, so /TimeOff/CreateTimeOff resolves the same as /TimeOff. No key
 * here is a prefix of another, so match order does not matter.
 */
export const DOC_LINKS = {
  '/staffmanagement':
    'https://zon.gitbook.io/zon-staff/zon-staff-management/how-to-add-both-shopify-and-non-shopify-staff-members-in-zon-staff-management',
  '/reports':
    'https://zon.gitbook.io/zon-staff/zon-staff-management/how-do-staffs-reports-and-analytics-be-accessed-in-zon',
  '/payroll':
    'https://zon.gitbook.io/zon-staff/zon-staff-management/how-can-a-payroll-be-accurately-configured-and-recorded-for-a-staff-member',
  '/settings':
    'https://zon.gitbook.io/zon-staff/zon-staff-management/how-do-settings-streamline-payroll-scheduling-and-leave-logic',
  // The live Schedule page linked to https://docs.example.com/schedule - a
  // placeholder that has been shipping to users. This is the real page.
  '/schedule':
    'https://zon.gitbook.io/zon-staff/zon-staff-management/how-to-configure-and-implement-staff-schedules-in-zon',
  '/tasklist':
    'https://zon.gitbook.io/zon-staff/zon-staff-management/how-are-tasks-formulated-and-allocated-to-staff-members-through-zon',
  '/timeoff':
    'https://zon.gitbook.io/zon-staff/zon-staff-management/how-are-time-off-policies-outlined-and-requests-managed-in-zon',
};

/** The doc for a route, falling back to the setup guide on pages without one. */
export function docUrlForPath(pathname) {
  const path = String(pathname || '').toLowerCase();
  const match = Object.keys(DOC_LINKS).find((prefix) => path.startsWith(prefix));

  return match ? DOC_LINKS[match] : DOCS_URL;
}
